import React, { createContext, useContext, useState, useMemo } from 'react';
import {
  RANGE_PRESETS,
  DATE_OPTIONS,
  DEFAULT_PRESET,
  AE_RETENTION_DAYS,
  normalizePresetId,
  resolveRange,
} from '../lib/rangePresets';
import {
  SEGMENT_OPTIONS,
  DEFAULT_SEGMENT,
  normalizeSegment,
} from '../lib/segment';

// Preset + range-resolution logic lives in src/lib/rangePresets.js (pure, so it
// is unit-testable); re-exported here for existing consumers that import them
// from the context module (e.g. RangePicker, InsightsStrip).
export {
  RANGE_PRESETS,
  DATE_OPTIONS,
  DEFAULT_PRESET,
  AE_RETENTION_DAYS,
  normalizePresetId,
  resolveRange,
};

// Same pattern for the visitor-segment table/default (src/lib/segment.js), so the
// Header control can import the options straight from the context module.
export { SEGMENT_OPTIONS, DEFAULT_SEGMENT, normalizeSegment };

const FilterContext = createContext(null);

export const SITE_OPTIONS = [
  { id: 'all', name: 'All Network Sites' },
  { id: 'publicark.com', name: 'PublicArk (publicark.com)' },
  { id: '0mod.com', name: '0mod Platform (0mod.com)' },
  { id: 'www.0mod.com', name: '0mod Web (www.0mod.com)' },
  { id: 'dailyjokecalendar.com', name: 'Daily Joke Calendar' },
  { id: 'topazly.com', name: 'Topazly (topazly.com)' },
  { id: 'quipcam.com', name: 'QuipCam (quipcam.com)' },
  { id: 'jeremyfletcher.com', name: 'Jeremy Fletcher (jeremyfletcher.com)' },
  { id: 'api.0mod.com', name: 'x402 Gateway (api.0mod.com)' },
];

export function FilterProvider({ children }) {
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedPreset, setSelectedPresetState] = useState(() => {
    try {
      const saved = localStorage.getItem('topazly_selected_preset');
      if (saved) return normalizePresetId(saved);
    } catch {}
    return DEFAULT_PRESET;
  });
  const setSelectedPreset = (value) => {
    const normalized = normalizePresetId(value);
    setSelectedPresetState(normalized);
    try {
      localStorage.setItem('topazly_selected_preset', normalized);
    } catch {}
  };
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [humansOnly, setHumansOnly] = useState(true);
  // Visitor segment (All / Non-admin / Admin). Kept in the global provider — not
  // in a tab — so the selection persists across tab switches, exactly like the
  // range preset. Defaults to 'non-admin' (see lib/segment.js for the rationale:
  // the owner's own browsing is noise). The setter normalizes so a bad value can
  // never be stored, and therefore never serialized to the API as a 400.
  const [segment, setSegmentState] = useState(DEFAULT_SEGMENT);
  const setSegment = (value) => setSegmentState(normalizeSegment(value));
  const [countryFilter, setCountryFilter] = useState('');
  const [pathQuery, setPathQuery] = useState('');
  const [activeTab, setActiveTab] = useState('explorer');

  const baseRange = useMemo(
    () => resolveRange(selectedPreset, customFrom, customTo),
    [selectedPreset, customFrom, customTo]
  );

  // The `range` handed to panels carries comparison dates only when the toggle
  // is on, so every consumer stays consistent with the server request.
  const range = useMemo(
    () =>
      compareEnabled
        ? baseRange
        : { ...baseRange, compareFrom: null, compareTo: null },
    [baseRange, compareEnabled]
  );

  // Kept for any consumer still reading `selectedDays` — derived, not state.
  const selectedDays = baseRange.days;
  const setSelectedDays = (value) => setSelectedPreset(normalizePresetId(value));

  return (
    <FilterContext.Provider value={{
      selectedSite,
      setSelectedSite,
      // New global range API
      range,
      selectedPreset,
      setSelectedPreset,
      customFrom,
      setCustomFrom,
      customTo,
      setCustomTo,
      compareEnabled,
      setCompareEnabled,
      // Backward-compatible aliases
      selectedDays,
      setSelectedDays,
      humansOnly,
      setHumansOnly,
      // Visitor segment (global, persists across tabs). Consumers pass it to the
      // engagement fetch; non-segmentable tabs disable the control (see
      // lib/segment.js isSegmentableTab).
      segment,
      setSegment,
      countryFilter,
      setCountryFilter,
      pathQuery,
      setPathQuery,
      activeTab,
      setActiveTab,
    }}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
